import { useCallback, useEffect, useState } from "react";
import { Archive, FolderCog, Loader2, Plus } from "lucide-react";
import { staffApi, errMsg, fmtDate, saveBlob } from "../api";
import { photoSrc } from "@/components/news/newsUtils";
import { ArticleEditor } from "./ArticleEditor";
import { CategoryManager } from "./CategoryManager";

const badge = (a) => {
  if (a.status !== "published") return ["Draft", "bg-mist text-navy"];
  if (new Date(a.publish_at) > new Date()) return ["Scheduled", "bg-gold/20 text-gold-deep"];
  return ["Published", "bg-navy text-paper"];
};

const ArticleRow = ({ a, onOpen }) => {
  const [label, cls] = badge(a);
  return (
    <li>
      <button type="button" onClick={onOpen} className="grid w-full grid-cols-[72px_1fr] items-center gap-4 p-4 text-left transition-colors hover:bg-mist/50 sm:grid-cols-[96px_1fr_auto]" data-testid={`news-admin-row-${a.slug}`}>
        <span className="block aspect-[3/2] overflow-hidden bg-mist">{a.featured && <img src={photoSrc(a.featured.url_sm)} alt="" className="h-full w-full object-cover" />}</span>
        <span>
          <span className="block font-semibold text-navy">{a.title}</span>
          <span className="mt-1 block text-xs text-slate">{a.category?.name} · Edited {fmtDate(a.updated_at)}{a.event && " · Event"}</span>
        </span>
        <span className={`col-start-2 w-fit px-2.5 py-1 text-xs font-bold uppercase tracking-wider sm:col-start-auto ${cls}`} data-testid={`news-admin-status-${a.slug}`}>{label}</span>
      </button>
    </li>
  );
};

export const NewsAdminPanel = () => {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, "new" or article id
  const [showCats, setShowCats] = useState(false);
  const [error, setError] = useState("");
  const [backup, setBackup] = useState(false);

  const load = useCallback(() => {
    Promise.all([staffApi.get("/news"), staffApi.get("/news/categories")])
      .then(([a, c]) => { setItems(Array.isArray(a.data) ? a.data : []); setCats(Array.isArray(c.data) ? c.data : []); })
      .catch(async (e) => setError(await errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setBackup(true);
    try {
      const r = await staffApi.get("/news/backup", { responseType: "blob" });
      saveBlob(r.data, `frf-news-backup-${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (e) { setError(await errMsg(e, "Backup failed. Please try again.")); } finally { setBackup(false); }
  };

  if (error) return <p className="text-sm text-fire" role="alert">{error}</p>;
  if (!items) return <Loader2 className="animate-spin text-navy" />;
  if (editing) return <ArticleEditor key={editing} articleId={editing === "new" ? null : editing} cats={cats} onBack={() => { setEditing(null); load(); }} />;
  return (
    <div className="space-y-6" data-testid="news-admin-panel">
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => setEditing("new")} disabled={!cats.length} className="btn btn-navy" data-testid="news-admin-new-button"><Plus size={16} /> New Article or Event</button>
        <button type="button" onClick={() => setShowCats(!showCats)} className="btn btn-ghost" data-testid="news-admin-categories-button"><FolderCog size={16} /> Categories</button>
        <button type="button" onClick={download} disabled={backup} className="btn btn-ghost disabled:opacity-60" data-testid="news-admin-backup-button">
          {backup ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />} Download Full Backup
        </button>
      </div>
      {showCats && <CategoryManager cats={cats} onChanged={load} />}
      {items.length ? (
        <ul className="divide-y divide-navy/10 border border-navy/10 bg-white">{items.map((a) => <ArticleRow key={a.id} a={a} onOpen={() => setEditing(a.id)} />)}</ul>
      ) : (
        <p className="border border-navy/10 bg-white p-6 text-sm text-slate" data-testid="news-admin-empty">No articles yet. Click "New Article or Event" to write your first one.</p>
      )}
      <p className="text-xs text-slate">Drafts and scheduled articles are only visible here. The backup is a zip file of every article and photo. Keep a copy on your computer.</p>
    </div>
  );
};
